import React, { useEffect, useRef, useState } from 'react';
import ModuleTopExtras from '../ModuleTopExtras';
import { useLanguage } from '../../i18n/LanguageContext';
import { API_ENDPOINTS, apiRequest } from '../../config/api';
import MathText from '../../utils/MathText';
import {
  ExamLanguage,
  ExamPaper,
  ExamPaperQuestions,
  paperTotalMarks,
  paperQuestionCount,
} from '../../utils/examPaper';
import { exportExamPapersPdf } from '../../utils/examPaperExport';
import { AdProcessingBanner } from '../ads/Ads';
import './Modules.css';
import './ExamModule.css';

// --------------------------------------------------------------
// Constants
// --------------------------------------------------------------

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB
const ACCEPTED_EXT = ['.pdf', '.docx', '.pptx', '.ppt', '.png', '.jpg', '.jpeg', '.webp', '.gif', '.bmp'];

const LANGUAGE_OPTIONS: { value: ExamLanguage; label: string }[] = [
  { value: 'english', label: 'English' },
  { value: 'traditional_chinese', label: '繁體中文 (Traditional Chinese)' },
  { value: 'simplified_chinese', label: '简体中文 (Simplified Chinese)' },
  { value: 'auto', label: 'Auto-detect' },
];

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

// Progress easing (same curve as NotesModule): keeps moving for long vision calls.
const PROGRESS_HEAD_START = 6;
const PROGRESS_CEILING = 90;
const PROGRESS_TIME_CONSTANT_MS = 26000;

const statusForProgress = (pct: number): string => {
  if (pct < 30) return 'Reading every page with the AI vision model…';
  if (pct < 60) return 'Understanding the material and tables…';
  if (pct < 82) return 'Setting the question paper and model answers…';
  return 'Still working — long documents take a little longer…';
};

const normalizePaper = (data: any): ExamPaper => ({
  title: data?.title || 'Exam Paper',
  language: data?.language || 'auto',
  questions: {
    mcq: Array.isArray(data?.questions?.mcq) ? data.questions.mcq : [],
    tf: Array.isArray(data?.questions?.tf) ? data.questions.tf : [],
    matching: Array.isArray(data?.questions?.matching) ? data.questions.matching : [],
    short: Array.isArray(data?.questions?.short) ? data.questions.short : [],
  },
  pages_read: data?.pages_read,
  total_pages: data?.total_pages,
  truncated: data?.truncated,
  method: data?.method,
  source_word_count: data?.source_word_count,
});

// --------------------------------------------------------------
// Paper sheets (shared by the on-screen preview and print/PDF)
// --------------------------------------------------------------

const SheetHeader: React.FC<{ paper: ExamPaper; kind: 'questions' | 'answers' }> = ({ paper, kind }) => (
  <div className="exam-sheet-header">
    <h2 className="exam-sheet-title"><MathText text={paper.title} /></h2>
    <div className="exam-sheet-subtitle">{kind === 'questions' ? 'Question Paper' : 'Answer Paper'}</div>
    <div className="exam-sheet-meta">Total marks: {paperTotalMarks(paper.questions)}</div>
    {kind === 'questions' && (
      <div className="exam-sheet-name-line">
        Name: ________________________&nbsp;&nbsp;&nbsp;Class: __________&nbsp;&nbsp;&nbsp;Date: __________
      </div>
    )}
  </div>
);

const QuestionSheet: React.FC<{ paper: ExamPaper }> = ({ paper }) => {
  const q = paper.questions;
  let sectionCode = 64; // A, B, C, D…

  const sections: { key: keyof ExamPaperQuestions; title: string }[] = [];
  if (q.mcq.length) sections.push({ key: 'mcq', title: 'Multiple Choice' });
  if (q.tf.length) sections.push({ key: 'tf', title: 'True or False' });
  if (q.matching.length) sections.push({ key: 'matching', title: 'Matching' });
  if (q.short.length) sections.push({ key: 'short', title: 'Short Questions' });

  return (
    <div className="exam-sheet">
      <SheetHeader paper={paper} kind="questions" />

      {sections.map((section) => {
        sectionCode += 1;
        const code = LETTERS[sectionCode - 1];

        if (section.key === 'mcq') {
          const totalMarks = q.mcq.length;
          return (
            <section key="mcq" className="exam-section">
              <h3>Section {code} — {section.title} <span className="exam-marks">({totalMarks} marks)</span></h3>
              <p className="exam-instruction">Choose the best answer for each question and write its letter in the bracket.</p>
              {q.mcq.map((item, i) => (
                <div key={i} className="exam-question">
                  <div className="exam-q-text"><span className="exam-q-num">{i + 1}.</span> <MathText text={item.question} /> <span className="exam-answer-slot">( &nbsp;&nbsp;&nbsp; )</span></div>
                  <div className="exam-options">
                    {item.options.map((opt, oi) => (
                      <div key={oi} className="exam-option"><span className="exam-option-letter">{LETTERS[oi]}.</span> <MathText text={opt} /></div>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          );
        }

        if (section.key === 'tf') {
          return (
            <section key="tf" className="exam-section">
              <h3>Section {code} — {section.title} <span className="exam-marks">({q.tf.length} marks)</span></h3>
              <p className="exam-instruction">Write “T” for True or “F” for False in the bracket at the end of each statement.</p>
              {q.tf.map((item, i) => (
                <div key={i} className="exam-question">
                  <div className="exam-q-text"><span className="exam-q-num">{i + 1}.</span> <MathText text={item.question} /> <span className="exam-answer-slot">( &nbsp;&nbsp;&nbsp; )</span></div>
                </div>
              ))}
            </section>
          );
        }

        if (section.key === 'matching') {
          const totalPairs = q.matching.reduce((acc, g) => acc + g.items.length, 0);
          return (
            <section key="matching" className="exam-section">
              <h3>Section {code} — {section.title} <span className="exam-marks">({totalPairs} marks)</span></h3>
              {q.matching.map((group, gi) => (
                <div key={gi} className="exam-matching-group">
                  <p className="exam-instruction">{group.instruction || 'Match each item on the left with the correct option on the right.'}</p>
                  <div className="exam-matching-options">
                    {group.options.map((opt, oi) => (
                      <span key={oi} className="exam-matching-option"><strong>{LETTERS[oi]}.</strong> <MathText text={opt} /></span>
                    ))}
                  </div>
                  <div className="exam-matching-items">
                    {group.items.map((item, ii) => (
                      <div key={ii} className="exam-matching-item">
                        <span className="exam-q-num">{ii + 1}.</span>
                        <span className="exam-matching-left"><MathText text={item.left} /></span>
                        <span className="exam-matching-blank">__________</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          );
        }

        const shortMarks = q.short.reduce((acc, x) => acc + (x.marks || 0), 0);
        return (
          <section key="short" className="exam-section">
            <h3>Section {code} — {section.title} <span className="exam-marks">({shortMarks} marks)</span></h3>
            {q.short.map((item, i) => (
              <div key={i} className="exam-question">
                <div className="exam-q-text">
                  <span className="exam-q-num">{i + 1}.</span> <MathText text={item.question} />
                  <span className="exam-marks-inline">({item.marks} marks)</span>
                </div>
                <div className="exam-answer-lines" style={{ ['--lines' as any]: Math.max(2, Math.min(Math.ceil((item.marks || 3) / 2) + 1, 4)) }} />
              </div>
            ))}
          </section>
        );
      })}

      <div className="exam-sheet-end">— End of Question Paper —</div>
    </div>
  );
};

const AnswerSheet: React.FC<{ paper: ExamPaper }> = ({ paper }) => {
  const q = paper.questions;
  let sectionCode = 64;

  const sections: { key: keyof ExamPaperQuestions; title: string }[] = [];
  if (q.mcq.length) sections.push({ key: 'mcq', title: 'Multiple Choice: Answers' });
  if (q.tf.length) sections.push({ key: 'tf', title: 'True or False: Answers' });
  if (q.matching.length) sections.push({ key: 'matching', title: 'Matching: Answers' });
  if (q.short.length) sections.push({ key: 'short', title: 'Short Questions: Model Answers' });

  return (
    <div className="exam-sheet">
      <SheetHeader paper={paper} kind="answers" />

      {sections.map((section) => {
        sectionCode += 1;
        const code = LETTERS[sectionCode - 1];

        if (section.key === 'mcq') {
          return (
            <section key="mcq" className="exam-section">
              <h3>Section {code} — {section.title}</h3>
              {q.mcq.map((item, i) => (
                <div key={i} className="exam-answer-row">
                  <div className="exam-q-text"><span className="exam-q-num">{i + 1}.</span> <MathText text={item.question} /></div>
                  <div className="exam-answer">
                    <strong>Answer: {LETTERS[item.answer_index]}</strong>
                    {item.explanation && <span className="exam-explanation"> — <MathText text={item.explanation} /></span>}
                  </div>
                </div>
              ))}
            </section>
          );
        }

        if (section.key === 'tf') {
          return (
            <section key="tf" className="exam-section">
              <h3>Section {code} — {section.title}</h3>
              {q.tf.map((item, i) => (
                <div key={i} className="exam-answer-row">
                  <div className="exam-q-text"><span className="exam-q-num">{i + 1}.</span> <MathText text={item.question} /></div>
                  <div className="exam-answer">
                    <strong>Answer: {item.answer ? 'T (True)' : 'F (False)'}</strong>
                    {item.explanation && <span className="exam-explanation"> — <MathText text={item.explanation} /></span>}
                  </div>
                </div>
              ))}
            </section>
          );
        }

        if (section.key === 'matching') {
          return (
            <section key="matching" className="exam-section">
              <h3>Section {code} — {section.title}</h3>
              {q.matching.map((group, gi) => (
                <div key={gi} className="exam-answer-row">
                  {group.pairs
                    .slice()
                    .sort((a, b) => group.items.findIndex((x) => x.key === a.key) - group.items.findIndex((x) => x.key === b.key))
                    .map((pair) => {
                      const item = group.items.find((x) => x.key === pair.key);
                      if (!item) return null;
                      const opt = group.options[pair.answer_index];
                      return (
                        <div key={pair.key} className="exam-answer">
                          <span className="exam-q-num">{group.items.indexOf(item) + 1}.</span>{' '}
                          <MathText text={item.left} /> → <strong>{LETTERS[pair.answer_index]}) <MathText text={opt} /></strong>
                        </div>
                      );
                    })}
                </div>
              ))}
            </section>
          );
        }

        return (
          <section key="short" className="exam-section">
            <h3>Section {code} — {section.title}</h3>
            {q.short.map((item, i) => (
              <div key={i} className="exam-answer-row">
                <div className="exam-q-text">
                  <span className="exam-q-num">{i + 1}.</span> <MathText text={item.question} />
                  <span className="exam-marks-inline">({item.marks} marks)</span>
                </div>
                <div className="exam-answer"><strong>Model answer:</strong> <MathText text={item.answer} /></div>
                {item.points && <div className="exam-answer exam-points"><strong>Marking points:</strong> <MathText text={item.points} /></div>}
              </div>
            ))}
          </section>
        );
      })}

      <div className="exam-sheet-end">— End of Answer Paper —</div>
    </div>
  );
};

// --------------------------------------------------------------
// Main component
// --------------------------------------------------------------

const ExamModule: React.FC = () => {
  const { t } = useLanguage();
  const [currentView, setCurrentView] = useState<'input' | 'processing' | 'result'>('input');
  const [language, setLanguage] = useState<ExamLanguage>('english');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [paper, setPaper] = useState<ExamPaper | null>(null);
  const [activeTab, setActiveTab] = useState<'questions' | 'answers'>('questions');
  const [isExporting, setIsExporting] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const questionWrapRef = useRef<HTMLDivElement>(null);
  const answerWrapRef = useRef<HTMLDivElement>(null);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressStartRef = useRef<number>(0);

  const stopProgress = () => {
    if (progressTimerRef.current !== null) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  };

  const startProgress = () => {
    stopProgress();
    progressStartRef.current = Date.now();
    setProgress(PROGRESS_HEAD_START);
    setStatusMessage(statusForProgress(PROGRESS_HEAD_START));
    progressTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - progressStartRef.current;
      const pct = PROGRESS_HEAD_START +
        (PROGRESS_CEILING - PROGRESS_HEAD_START) *
        (1 - Math.exp(-elapsed / PROGRESS_TIME_CONSTANT_MS));
      setProgress(pct);
      setStatusMessage(statusForProgress(pct));
    }, 250);
  };

  useEffect(() => stopProgress, []);

  const stageFile = (file: File) => {
    const lower = file.name.toLowerCase();
    const okExt = ACCEPTED_EXT.some((ext) => lower.endsWith(ext));
    if (!okExt) {
      alert('Unsupported file. Please upload a PDF, Word, PowerPoint or image file.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      alert('File exceeds the 25 MB limit.');
      return;
    }
    setSelectedFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleGenerate = async () => {
    const file = selectedFile;
    if (!file) return;

    setCurrentView('processing');
    startProgress();

    const form = new FormData();
    form.append('file', file);
    form.append('language', language);

    try {
      const res = await apiRequest<Record<string, unknown>>(API_ENDPOINTS.EXAM_PAPER_GENERATE, {
        method: 'POST',
        body: form,
        timeout: 20 * 60 * 1000,
      });
      if (!res.data) throw new Error('The server returned an empty response.');
      setPaper(normalizePaper(res.data));
      setActiveTab('questions');
      setProgress(100);
      setCurrentView('result');
    } catch (err) {
      console.error('Exam paper generation failed', err);
      const serverMsg = err instanceof Error ? err.message : '';
      alert(
        (serverMsg && serverMsg !== 'API request failed' ? `${serverMsg}\n\n` : '') +
          'Could not build an exam paper from that file. Please make sure the file is readable and try again.'
      );
      setCurrentView('input');
    } finally {
      stopProgress();
      setIsDragging(false);
      setStatusMessage('');
    }
  };

  // Kept for when PDF export is re-enabled; the button is currently hidden.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleExportPdf = async () => {
    if (!paper || isExporting) return;
    const questionSheet = questionWrapRef.current?.querySelector<HTMLElement>('.exam-sheet') ?? null;
    const answerSheet = answerWrapRef.current?.querySelector<HTMLElement>('.exam-sheet') ?? null;
    setIsExporting(true);
    try {
      await exportExamPapersPdf(questionSheet, answerSheet, paper.title);
    } catch (err) {
      console.error('PDF export failed', err);
      alert('Failed to export the PDFs. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const reset = () => {
    setPaper(null);
    setSelectedFile(null);
    setProgress(0);
    setCurrentView('input');
  };

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------

  return (
    <div className="module-container exam-module-v2">
      <div className="module-header">
        <span className="module-icon">🎓</span>
        <h2 className="module-title">{t('moduleHeaders.exam.title')}</h2>
        <p className="module-subtitle">{t('moduleHeaders.exam.subtitle')}</p>
      </div>

      <ModuleTopExtras moduleId="exam" />

      <div className="module-content">
        {currentView === 'input' && (
          <div className="content-section">
            <div className="section-header">
              <h3>Create an exam paper</h3>
              <p>PDF, Word, PowerPoint or image · up to 25 MB · AI vision read, including scans and diagrams.</p>
            </div>

            <div className="form-group">
              <label className="form-label">Question Language</label>
              <select
                className="exam-language-select"
                value={language}
                onChange={(e) => setLanguage(e.target.value as ExamLanguage)}
              >
                {LANGUAGE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Upload file</label>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept={ACCEPTED_EXT.join(',')}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) stageFile(f);
                }}
              />
              <div
                className={`file-upload${isDragging ? ' dragover' : ''}`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  const f = e.dataTransfer.files?.[0];
                  if (f) stageFile(f);
                }}
              >
                <span className="file-upload-icon">📄</span>
                <div className="file-upload-text">Drag &amp; drop your file here</div>
                <div className="file-upload-hint">
                  {selectedFile ? selectedFile.name : 'or click to browse — PDF, Word, PowerPoint or image'}
                </div>
                <span className="file-upload-button">{selectedFile ? 'Replace file' : 'Choose file'}</span>
              </div>
            </div>

            {selectedFile && (
              <div className="generation-actions" style={{ flexDirection: 'column', gap: '12px' }}>
                <button className="btn btn-primary" onClick={handleGenerate}>
                  Generate exam paper
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                >
                  Remove file
                </button>
              </div>
            )}
          </div>
        )}

        {currentView === 'processing' && (
          <div className="content-section">
            <div className="section-header">
              <h3>Building your exam paper</h3>
              {selectedFile && <p className="file-name" style={{ marginBottom: 0 }}>{selectedFile.name}</p>}
            </div>
            <AdProcessingBanner active={currentView === 'processing'} />
            <div className="progress-container">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${Math.round(progress)}%` }}></div>
              </div>
              <div className="progress-message">{statusMessage} {Math.round(progress)}%</div>
            </div>
          </div>
        )}

        {currentView === 'result' && paper && (
          <div className="content-section exam-paper-result">
            <div className="section-header">
              <h3>{paper.title}</h3>
              <p style={{ margin: 0 }}>
                {paperQuestionCount(paper.questions)} questions · {paperTotalMarks(paper.questions)} marks
                {typeof paper.pages_read === 'number' && (
                  <> · read {paper.pages_read} of {paper.total_pages ?? paper.pages_read} page
                    {(paper.total_pages ?? paper.pages_read) !== 1 ? 's' : ''}
                    {paper.truncated ? ' (only the first pages were processed)' : ''}
                  </>
                )}
              </p>
            </div>

            <div className="result-actions exam-paper-toolbar">
              {/* PDF export button hidden for now — handler + examPaperExport util kept for reuse.
              <button className="btn btn-secondary" onClick={handleExportPdf} disabled={isExporting}>
                {isExporting ? 'Preparing PDFs…' : '⬇ Export PDF (Q + A)'}
              </button>
              */}
              <button className="btn btn-secondary" onClick={() => window.print()}>
                🖨 Print / Save PDF
              </button>
              <button className="btn btn-primary" onClick={reset}>Create new paper</button>
            </div>

            <div className="exam-tabs no-print">
              <button
                className={`exam-tab${activeTab === 'questions' ? ' active' : ''}`}
                onClick={() => setActiveTab('questions')}
              >
                Question Paper
              </button>
              <button
                className={`exam-tab${activeTab === 'answers' ? ' active' : ''}`}
                onClick={() => setActiveTab('answers')}
              >
                Answer Paper
              </button>
            </div>

            {/* Both sheets stay mounted: the active tab shows on screen, and the
                print stylesheet prints the question paper then the answer paper. */}
            <div className="exam-papers">
              <div
                ref={questionWrapRef}
                className={`exam-sheet-wrap${activeTab === 'questions' ? ' is-active' : ''}`}
                data-sheet="questions"
              >
                <QuestionSheet paper={paper} />
              </div>
              <div
                ref={answerWrapRef}
                className={`exam-sheet-wrap${activeTab === 'answers' ? ' is-active' : ''}`}
                data-sheet="answers"
              >
                <AnswerSheet paper={paper} />
              </div>
            </div>

            <div className="result-actions no-print" style={{ marginTop: 16 }}>
              {/* PDF export button hidden for now (see top toolbar).
              <button className="btn btn-secondary" onClick={handleExportPdf} disabled={isExporting}>
                {isExporting ? 'Preparing PDFs…' : '⬇ Export PDF (Q + A)'}
              </button>
              */}
              <button className="btn btn-primary" onClick={reset}>Create new paper</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExamModule;
